import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { CustomViewAttribute, CustomViewFormModel } from 'src/graphql/entities/custom-view';
import { capitalizeString } from 'src/utils/strings';
import { useScreenDimensions } from 'src/utils/system';
import { DraggableList } from './draggable-list';

interface CustomViewColumnsModalProps {
  onClose: () => void;
  attributes: CustomViewAttribute[];
  selectedColumns: CustomViewFormModel['columns'];
  anchorEl?: HTMLButtonElement | null;
  onReorder: (data: CustomViewFormModel['columns']) => void;
  onSelect: (column: CustomViewAttribute) => void;
}

// TODO refactor so this component can be used with react-hook-form
// so it is possible to use it with both raw react handlers and react-hook forms
export const CustomViewsColumnsSelector = ({
  anchorEl,
  onClose,
  attributes,
  selectedColumns,
  onSelect,
  onReorder,
}: CustomViewColumnsModalProps) => {
  const [filterString, setFilterString] = useState('');
  const { isMobile } = useScreenDimensions();

  const groupedOptions = useMemo(
    () =>
      Object.entries(
        attributes
          ?.filter(
            // filters items which match the search
            (option) =>
              !filterString ||
              option.attributeLabel?.toLowerCase().includes(filterString.toLowerCase()),
          )
          .reduce<Record<string, CustomViewAttribute[]>>(
            // groups matched items by their group
            (acc, i) => ({
              ...acc,
              [i.group]: !acc?.[i.group] ? [i] : [...acc[i.group], i],
            }),
            {},
          ) ?? {},
      ),
    [filterString, attributes],
  );

  return (
    <Popover
      open={!!anchorEl}
      onClose={onClose}
      anchorEl={anchorEl}
      sx={{
        maxHeight: isMobile ? '100vh' : '40rem',
        minWidth: isMobile ? '100vw' : '40rem',
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: isMobile ? '100vw' : '30rem',
            maxWidth: isMobile ? '100vw' : '40rem',
            maxHeight: isMobile ? '100vh' : '40rem',
            borderRadius: isMobile ? 0 : '0.8rem',
            // neither anchorOrigin nor transformOrigin can't be set to be 0
            // so using important to override their values for mobile devices
            top: isMobile ? '0 !important' : undefined,
            left: isMobile ? '0 !important' : undefined,
          },
        },
      }}
      anchorOrigin={{
        vertical: isMobile ? 'top' : 'bottom',
        horizontal: isMobile ? 'center' : 'left',
      }}
      transformOrigin={{
        vertical: isMobile ? 'top' : 'top',
        horizontal: isMobile ? 'center' : 'right',
      }}
    >
      <>
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          padding="1rem"
        >
          <Typography variant="body2">DISPLAY</Typography>
          {isMobile ? (
            <Tooltip title="close">
              <IconButton onClick={onClose}>
                <DynamicIcon name="close" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>

        <Divider />

        <Box>
          <Stack>
            <Box
              padding="1rem"
              sx={{
                backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
              }}
            >
              <Typography variant="body2">Visible Attributes</Typography>
            </Box>

            {selectedColumns?.length ? (
              <DraggableList
                selectedColumns={selectedColumns}
                onSelect={onSelect}
                onReorder={onReorder}
              />
            ) : (
              <Typography variant="body2" sx={{ padding: '1rem' }}>
                Nothing selected
              </Typography>
            )}
          </Stack>

          <Divider sx={{ margin: '0.5rem 0' }} />
          <Box padding="1rem">
            <TextField
              fullWidth
              size="small"
              placeholder="Filter Results"
              value={filterString}
              onChange={(e) => setFilterString(e.target.value)}
              InputProps={{
                startAdornment: <DynamicIcon name="search" sx={{ marginRight: '0.5rem' }} />,
                endAdornment: null,
              }}
            />
          </Box>

          {groupedOptions.map(([group, options]) => (
            <Stack key={group}>
              <Box
                padding="1rem"
                sx={{
                  backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
                }}
              >
                <Typography variant="body2">{capitalizeString(group)}</Typography>
              </Box>
              {options.map((option) => {
                return (
                  <Stack key={option.attribute} flexDirection="row" alignItems="center">
                    <Checkbox
                      checked={selectedColumns?.some(
                        (selected) => selected.attribute === option.attribute,
                      )}
                      onChange={() => onSelect(option)}
                    />
                    <Tooltip title={option.attributeLabel}>
                      <Typography
                        variant="body2"
                        flex={1}
                        minWidth={0}
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                      >
                        {option.attributeLabel}
                      </Typography>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          ))}
        </Box>
      </>
    </Popover>
  );
};

export const CustomViewsColumnsSelectorForm = memo(CustomViewsColumnsSelector);
